/**
 * Validation Functions
 *
 * Pure functions that take a value and return true/false.
 * Organized into generic validators (domain-agnostic) and domain-specific validators.
 */

export interface CrossCellContext {
  /** Additional context data that validators might need (network, planIdentifier, etc.) */
  [key: string]: unknown;
}

// =============================================================================
// GENERIC VALIDATORS (100% domain-agnostic)
// =============================================================================

/**
 * Basic numeric validation
 */
export const number = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const num = Number(value);
  return !isNaN(num) && isFinite(num);
};

/**
 * Percentage validation (0-100)
 */
export const percentage = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

/**
 * Currency validation (non-negative numbers)
 */
export const currency = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0;
};

/**
 * Integer validation
 */
export const integer = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && Number.isInteger(num);
};

/**
 * Positive number validation
 */
export const positive = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && num > 0;
};

/**
 * Required field validation
 */
export const required = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

/**
 * Optional field validation (always passes)
 */
export const optional = (): boolean => true;

// =============================================================================
// HEALTHCARE/BTX DOMAIN-SPECIFIC VALIDATORS
// =============================================================================

/**
 * Age validation (0-150)
 */
export const age = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && Number.isInteger(num) && num >= 0 && num <= 150;
};

/**
 * Copay dollar amount validation (healthcare specific)
 */
export const copayDollar = (value: unknown): boolean => {
  const num = Number(value);
  if (isNaN(num) || num < 0) return false;

  // $0 copays are valid (preventive services)
  if (num === 0) return true;

  // Reasonable copay range
  return num <= 500;
};

/**
 * Copay percentage validation (less common but exists)
 */
export const copayPercentage = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

/**
 * Individual deductible validation
 */
export const deductibleIndividual = (value: unknown): boolean => {
  const num = Number(value);
  if (isNaN(num) || num < 0) return false;

  // $0 deductibles are valid
  if (num === 0) return true;

  // Reasonable range for individual deductibles
  return num <= 20000;
};

/**
 * Coinsurance validation (0-100%)
 */
export const coinsurance = (value: unknown): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100;
};

/**
 * Out-of-pocket maximum individual validation
 */
export const outOfPocketMaxIndividual = (value: unknown): boolean => {
  const num = Number(value);
  if (isNaN(num) || num <= 0) return false;

  // Reasonable range (generous upper limit)
  return num <= 50000;
};

/**
 * Benefit maximum validation
 */
export const benefitMaximum = (value: unknown): boolean => {
  const num = Number(value);
  if (isNaN(num) || num < 0) return false;

  // $0 means unlimited benefits
  if (num === 0) return true;

  // Reasonable range (up to $10M for life insurance, etc.)
  return num <= 10000000;
};

/**
 * Visit limit validation
 */
export const visitLimit = (value: unknown): boolean => {
  const num = Number(value);
  if (isNaN(num) || !Number.isInteger(num) || num < 0) return false;

  // 0 means unlimited visits
  if (num === 0) return true;

  // Reasonable range (1-365 visits per year)
  return num <= 365;
};

/**
 * Map of all available validators
 * Organized: Generic validators first, then domain-specific validators
 */
export const validators = {
  // Generic validators (100% domain-agnostic)
  number,
  percentage,
  currency,
  integer,
  positive,
  required,
  optional,

  // Healthcare/BTX domain-specific validators
  age,
  copayDollar,
  copayPercentage,
  deductibleIndividual,
  coinsurance,
  outOfPocketMaxIndividual,
  benefitMaximum,
  visitLimit,
} as const;

export type ValidatorName = keyof typeof validators;
export type ValidatorFunction = (
  value: unknown,
  context?: CrossCellContext,
) => boolean;
export type ValidatorMap = typeof validators;

/**
 * Get validator function by name
 */
export const getValidator = (name: ValidatorName): ValidatorFunction =>
  validators[name];

/**
 * Validate a value against multiple validators
 */
export const validateValue = (
  value: unknown,
  validatorNames: ValidatorName[],
  context?: CrossCellContext,
): { isValid: boolean; failedValidators: ValidatorName[] } => {
  const failedValidators: ValidatorName[] = [];

  for (const validatorName of validatorNames) {
    const validator = getValidator(validatorName);
    if (!validator(value, context)) {
      failedValidators.push(validatorName);
    }
  }

  return {
    isValid: failedValidators.length === 0,
    failedValidators,
  };
};
